import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Alert,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import { Google as GoogleIcon, Link as LinkIcon } from '@mui/icons-material';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { AuthService } from '../services/authService';
import { BetaAccessService } from '../services/betaAccessService';

interface AuthProps {
  user: any;
}

const Auth: React.FC<AuthProps> = ({ user }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkingAccount, setLinkingAccount] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Check beta access for sign up
      if (isSignUp) {
        const isBetaUser = await BetaAccessService.isEmailAllowed(email);
        if (!isBetaUser) {
          setError('This app is currently in beta. Your email is not on the access list. Please contact the administrator for access.');
          setLoading(false);
          return;
        }
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await AuthService.signInWithGoogle();
      
      if (!result.success) {
        setError(result.message || 'Failed to sign in with Google');
      } else if (result.requiresLinking) {
        // Offer to link accounts if user also has email/password account
        setLinkDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkAccount = async () => {
    setError('');
    setLinkingAccount(true);

    try {
      const result = await AuthService.linkGoogleAccount(linkPassword);
      
      if (result.success) {
        setLinkDialogOpen(false);
        setLinkPassword('');
        // Success message could be shown here
      } else {
        setError(result.message || 'Failed to link accounts');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLinkingAccount(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (user) {
    const hasGoogle = AuthService.hasGoogleProvider();
    const providers = AuthService.getAuthProviders();
    
    return (
      <Box sx={{ p: 2, textAlign: 'right' }}>
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
          <Typography variant="body2">
            {user.email}
          </Typography>
          {providers.map(provider => (
            <Chip 
              key={provider}
              label={provider === 'google.com' ? 'Google' : 'Email'}
              size="small"
              color={provider === 'google.com' ? 'primary' : 'default'}
              icon={provider === 'google.com' ? <GoogleIcon /> : undefined}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {!hasGoogle && providers.includes('password') && (
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={<LinkIcon />}
              onClick={() => setLinkDialogOpen(true)}
            >
              Link Google
            </Button>
          )}
          <Button variant="outlined" size="small" onClick={handleSignOut}>
            Sign Out
          </Button>
        </Box>

        {/* Link Account Dialog (for already signed-in users) */}
        <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)}>
          <DialogTitle>Link Google Account</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Link your Google account to sign in with either method in the future.
              Please enter your password to confirm.
            </Typography>
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              value={linkPassword}
              onChange={(e) => setLinkPassword(e.target.value)}
              margin="normal"
              autoFocus
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setLinkDialogOpen(false);
              setLinkPassword('');
              setError('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleLinkAccount} 
              variant="contained"
              disabled={!linkPassword || linkingAccount}
            >
              {linkingAccount ? <CircularProgress size={20} /> : 'Link Account'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h4" gutterBottom align="center">
          Trade Advisor
        </Typography>
        <Typography variant="h6" gutterBottom align="center">
          {isSignUp ? 'Create Account' : 'Sign In'}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Beta Access Only
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            disabled={loading}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            disabled={loading}
          />
          <Button
            fullWidth
            variant="contained"
            type="submit"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : (isSignUp ? 'Sign Up' : 'Sign In')}
          </Button>
        </form>

        <Divider sx={{ my: 2 }}>OR</Divider>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleSignIn}
          disabled={loading}
          sx={{ mb: 2 }}
        >
          {loading ? <CircularProgress size={20} /> : 'Sign in with Google'}
        </Button>

        <Button
          fullWidth
          onClick={() => setIsSignUp(!isSignUp)}
          disabled={loading}
        >
          {isSignUp
            ? 'Already have an account? Sign In'
            : "Don't have an account? Sign Up"}
        </Button>

        {/* Link Account Dialog (after Google sign-in) */}
        <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)}>
          <DialogTitle>Link Your Accounts?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              You signed in with Google, but you also have an email/password account with the same email.
              Would you like to link these accounts so you can sign in with either method?
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              This is optional - you can continue using either sign-in method separately.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLinkDialogOpen(false)}>
              Not Now
            </Button>
            <Button 
              onClick={() => {
                setLinkDialogOpen(false);
                // Show the link dialog for signed-in users
                // This would trigger the link flow in the signed-in UI
              }} 
              variant="contained"
            >
              Link Accounts
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default Auth;